import React, { useState, useEffect } from 'react';
import { businessInfo } from '../mockData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/98 backdrop-blur-[32px] shadow-lg' : 'bg-white/98 backdrop-blur-[32px]'
    } border-b border-[#006B3F]/[0.08]`}>
      {/* Desktop Header */}
      <div className="hidden lg:block">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-10 md:px-20 py-7">
          <div className="flex justify-between items-center gap-2">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src="/ma_logo_correct.png" 
                alt="M.A. Zulassungsdienst Logo" 
                className="h-14 sm:h-[62px] w-auto transition-transform duration-300 hover:scale-[1.03]"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="flex items-center gap-14">
              <a 
                href="/" 
                className="text-[#2A2A2A] hover:text-[#006B3F] transition-all duration-300 font-medium text-[15px] tracking-tight relative group"
              >
                Leistungen
                <span className="absolute bottom-[-6px] left-0 w-0 h-[2px] bg-gradient-to-r from-[#006B3F] to-[#7CB342] group-hover:w-full transition-all duration-400" />
              </a>
              <a 
                href="/evb-nummer" 
                className="text-[#2A2A2A] hover:text-[#006B3F] transition-all duration-300 font-medium text-[15px] tracking-tight relative group"
              >
                eVB-Nummer
                <span className="absolute bottom-[-6px] left-0 w-0 h-[2px] bg-gradient-to-r from-[#006B3F] to-[#7CB342] group-hover:w-full transition-all duration-400" />
              </a>
              <a 
                href="/versicherung" 
                className="text-[#2A2A2A] hover:text-[#006B3F] transition-all duration-300 font-medium text-[15px] tracking-tight relative group"
              >
                Versicherung
                <span className="absolute bottom-[-6px] left-0 w-0 h-[2px] bg-gradient-to-r from-[#006B3F] to-[#7CB342] group-hover:w-full transition-all duration-400" />
              </a>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-[#2A2A2A] hover:text-[#006B3F] transition-all duration-300 font-medium text-[15px] tracking-tight relative group"
              >
                Kontakt
                <span className="absolute bottom-[-6px] left-0 w-0 h-[2px] bg-gradient-to-r from-[#006B3F] to-[#7CB342] group-hover:w-full transition-all duration-400" />
              </button>
              <a
                href="https://wa.me/4915159182828?text=Hallo%2C%20ich%20ben%C3%B6tige%20eine%20eVB-Nummer%20f%C3%BCr%20meine%20Kfz-Zulassung."
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-[15px] bg-[#E53935] hover:bg-[#C62828] text-white rounded-[10px] font-semibold text-[15px] tracking-tight transition-all duration-[350ms] hover:translate-y-[-4px] hover:shadow-[0_16px_36px_rgba(229,57,53,0.4)] inline-flex items-center gap-2"
              >
                eVB-Nummer erhalten →
              </a>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Sticky Header */}
      <div className="lg:hidden">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center gap-3">
            {/* Logo - kompakter auf Mobile */}
            <div className="flex-shrink-0">
              <img 
                src="/ma_logo_correct.png" 
                alt="M.A. Zulassungsdienst" 
                className="h-10 sm:h-12 w-auto"
              />
            </div>

            {/* eVB Button - immer sichtbar */}
            <a
              href="https://wa.me/4915159182828?text=Hallo%2C%20ich%20ben%C3%B6tige%20eine%20eVB-Nummer%20f%C3%BCr%20meine%20Kfz-Zulassung."
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 px-4 sm:px-5 py-2.5 sm:py-3 bg-[#E53935] hover:bg-[#C62828] text-white rounded-lg font-semibold text-xs sm:text-sm tracking-tight transition-all duration-300 whitespace-nowrap shadow-md active:scale-95"
            >
              eVB anfordern
            </a>

            {/* Burger Menu */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-700 hover:text-[#006B3F] transition-colors flex-shrink-0"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="mt-4 pt-4 border-t border-[#006B3F]/[0.08] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg">
              <nav className="flex flex-col gap-3 px-2 pb-2">
                <a 
                  href="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-left text-gray-700 hover:text-[#006B3F] hover:bg-gray-50 transition-colors font-medium py-3 px-3 rounded-md"
                >
                  Leistungen
                </a>
                <a 
                  href="/evb-nummer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-left text-gray-700 hover:text-[#006B3F] hover:bg-gray-50 transition-colors font-medium py-3 px-3 rounded-md"
                >
                  eVB-Nummer
                </a>
                <a 
                  href="/versicherung"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-left text-gray-700 hover:text-[#006B3F] hover:bg-gray-50 transition-colors font-medium py-3 px-3 rounded-md"
                >
                  Versicherung
                </a>
                <button 
                  onClick={() => {
                    scrollToSection('contact');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-gray-700 hover:text-[#006B3F] hover:bg-gray-50 transition-colors font-medium py-3 px-3 rounded-md"
                >
                  Kontakt
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};